import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "primereact/column";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { FilterDialog } from "../../components/translation/FilterDialog";
import { TranslationsImporter } from "../../components/translation/TranslationsImporter";
import { useTranslations } from "../../hooks/useTranslations";
import { getGlobalConfigurationPageLabel } from "../../utils";
import { LabelObject } from "../../interfaces";
import { AppContext } from "../../context/index";
import { Button, InputText } from "iuvity-ui-components";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { MultiSelect } from 'primereact/multiselect'
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { formatStringToDate2 } from "../../helpers/formatDate";
import { EmptyTemplate } from "../../components/datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const formBlankValues = {
  functionality: [],
  key: "",
  esvalue: "",
  envalue: "",
  type: [],
  version: [],
};

export const AdminTranslationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedApp, selectedLabel, pageFilter, language, permissiondInfoByRole,
      infoObjRolesByTenantState: { dataRolesByTenantGlobal }},
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          functionality: pageFilter?.functionality || [],
          key: pageFilter?.key || "",
          esvalue: pageFilter?.esvalue || "",
          envalue: pageFilter?.envalue || "",
          type: pageFilter?.type || [],
          version: pageFilter?.version || [],
        }
      : formBlankValues,
  });

  const { control, setValue } = form;

  const { data, translationAction, reloadFn } = useTranslations("DATA");
  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  const [filterModalVisible, setFilterModalVisible] = useState<boolean>(false);
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const showGoToFunctionalities = dataRolesByTenantGlobal?.find((rol: any) => rol?.functionality?.code === "COD_FW");
  const customPageLabel = getGlobalConfigurationPageLabel(t, location.pathname);

  const columns = useMemo(() => {
    return [
      {
        field: "functionality",
        header: t("Translations.functionalityLabel"),
      },
      {
        field: "type",
        header: t("Translations.typeLabel"),
      },
      {
        field: "key",
        header: t("Translations.keyLabel"),
      },
      {
        field: "esvalue",
        header: t("Translations.esvalueLabel"),
      },
      {
        field: "envalue",
        header: t("Translations.envalueLabel"),
      },
      {
        field: "releaseversion",
        header: t("Translations.releaseversionLabel"),
      },
      {
        field: "createdat",
        header: t("Translations.createdatLabel"),
      },
      {
        field: "updatedat",
        header: t("Translations.updatedatLabel"),
      },
      {
        field: "actions",
        header: t("Translations.actionsLabel"),
      },
    ];
  }, [t]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    functionality: { value: pageFilter?.functionality, matchMode: FilterMatchMode.IN },
    key: { value: pageFilter?.key, matchMode: FilterMatchMode.IN },
    esvalue: { value: pageFilter?.esvalue, matchMode: FilterMatchMode.CONTAINS },
    envalue: { value: pageFilter?.envalue, matchMode: FilterMatchMode.CONTAINS },
    type: { value: pageFilter?.type, matchMode: FilterMatchMode.IN },
    releaseversion: { value: pageFilter?.releaseversion, matchMode: FilterMatchMode.IN },
    createdat: { value: pageFilter?.createdat, matchMode: FilterMatchMode.IN },
    updatedat: { value: pageFilter?.updatedat, matchMode: FilterMatchMode.IN },
  };

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);

  const formOptions = useMemo(() => {
    if (data) {
      return {
        functionality: [...new Set(data.flatMap((item: any) => item.widgets))]
          .map((item) => ({
            value: item,
            label: `${item}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        type: [...new Set(data.flatMap((item: any) => item.type))]
          .map((item) => ({
            value: item,
            label: t(`Translations.${`${item}`.toLowerCase()}OptionLabel`),
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        version: [...new Set(data.map((item: any) => item.releaseversion))].map(
          (item) => ({
            value: `${item}`,
            label: `${item}`,
          })
        ),
      };
    } else {
      return;
    }
  }, [t, data]);

  const actionEditDetailFn = (label: LabelObject, actionType: 'edit' | 'detail') => {
    setAppState((prev) => ({
      ...prev,
      selectedLabel: label,
    }));
    navigate(`${location.pathname}/${actionType}`);
    window.scrollTo(0, 0);
  };

  const actionDeleteFn = async (label: LabelObject) => {
    await translationAction("DELETE", label.id);
    reloadFn();
  };

  const onDownloadTranslations = async () => {
    const matchKeys = {
      functionality: "widgets",
      type: "type",
      version: "releaseversion",
    };
    const itemsToFilter = Object.fromEntries(
      Object.entries(form.getValues())
        .filter(([_, value]) => value.length > 0)
        .map(([key, value]) => [(matchKeys as any)[key], value])
    );
    await translationAction("DOWNLOAD", {
      appid: selectedApp?.appid,
      filename: `${selectedApp?.tenantname
        .replaceAll(" ", "")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())}_${selectedApp?.appname
        .replaceAll(" ", "")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())}_${selectedApp?.env.slice(
        0,
        3
      )}_${t("Translations.translationsTitleFront").replaceAll(" ", "")}_${
        new Date().toISOString().substring(0, 10).replace(/-|:/g, "") +
        "-" +
        new Date().toLocaleTimeString("es-ES").replace(/-|:/g, "")
      }`,
      filtersInfo: Object.keys(itemsToFilter).length > 0 && itemsToFilter,
    });
  };

  const onClearFilters = () => {
    setFilteredData(data);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  const handleModalProps = async (
    type: "DOWNLOAD" | "DELETE" | "IMPORT",
    label?: LabelObject
  ) => {
    switch (type) {
      case "DOWNLOAD":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Translations.downloadModalTitle"),
          content: t("Translations.downloadModalContent"),
          onConfirm: () => onDownloadTranslations(),
        }));
      case "DELETE":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Translations.deleteModalTitle"),
          content: `${t("Translations.deleteModalContent1")} "${
            label?.key
          }" ${t("Translations.deleteModalContent2")}`,
          onConfirm: () => actionDeleteFn(label!),
        }));

      default:
        return;
    }
  };

  useEffect(() => {
    if (data && !pageFilter) {
      setFilteredData(data);
    } else if (!data) {
      setFilteredData(null);
    }
  }, [data, setFilteredData, setAppState, selectedLabel, pageFilter]);

  useLayoutEffect(() => {
    if (data && pageFilter) {
      const filterWidgets = form.getValues("functionality");
      const filterTypes = form.getValues("type");
      const filterVersion = form.getValues("version");
      setFilteredData(
        data.filter(
          (item: any) =>
            (form.getValues("functionality").length > 0
              ? item.widgets.some(
                  (wid: any[]) => filterWidgets.indexOf(wid) !== -1
                )
              : true) &&
            (form.getValues("type").length > 0
              ? filterTypes.includes(item.type)
              : true) &&
            (form.getValues("version").length > 0
              ? filterVersion.includes(item.releaseversion)
              : true)
        )
      );
    } else if (data) {
      setFilteredData(data);
    }
  }, [data, setFilteredData, form, pageFilter]);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Translations.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Translations.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Translations.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    const field = options.field === 'functionality' ? 'widgets' : options.field;
    const arrayOriginal = data
      ?.map((label: any) => ({
      ...label,
      type: t(`Translations.${label?.type.toLowerCase()}OptionLabel`),
      createdat: formatStringToDate2(label?.createdat ?? null, true),
      updatedat: formatStringToDate2(label?.updatedat ?? null, true),
      }))
      ?.map((item: any) => (
        {
          label: Array.isArray(item[`${field}`]) ? item[`${field}`][0] : item[`${field}`], 
          value: Array.isArray(item[`${field}`]) ? item[`${field}`][0] : item[`${field}`]
        }))
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Translations.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Translations.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("Translations.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };
  
  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.applyFilterLabel")}
        className="p-button"
        onClick={()=>{
          options.filterApplyCallback();
          setAppState((prev: any)=>({
            ...prev,
            // pageFilter: getValues(),
          }));
        }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFiltersColumn}
      />
    )
  };

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  useEffect(()=>{
    !!data && !!sessionStorage?.getItem('DOWNLOAD::TEXTMSG') && (()=>{
      sessionStorage.removeItem('DOWNLOAD::TEXTMSG');
      document.getElementById('222')?.click();
    })();
  },[data]);

  return (
    <>
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("Translations.translationsTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={customPageLabel}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => {
              navigate("/");
              onClearFilters();
            },
          }}
          breadcrumbsItems={[
            {
              label: t("Menu.SetupLabel"),
              disabled: true
            },
            {
              label: t("Translations.translationsTitleFront"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Translations.translationsDescription")}</span>
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        className="multiactions-c"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "444",
            icon: "iu iu-icon-cellphone",
            label: t("Translations.goFunctionalitiesLabel"),
            onClick: () => navigate("/widgets"),
            style: {
              width: "10rem !important",
              display: !showGoToFunctionalities ? 'none' : undefined,
            },
          },
          {
            id: "222",
            icon: "iu iu-icon-upload",
            label: t("Translations.downloadBtnLabel"),
            onClick: () => handleModalProps("DOWNLOAD"),
            style: { width: "10rem !important" },
            disabled: (!permissiondInfoByRole.READ && !permissiondInfoByRole.CREATE && !permissiondInfoByRole.UPDATE) ||
                      (!!(filteredData && filteredData?.length === 0) || !data),
          },
          {
            id: "333",
            icon: "iu iu-icon-download",
            label: t("Translations.importTranslationsTitle"),
            onClick: () => navigate("/translations/import"),
            style: { width: "10rem !important" },
            disabled: (!permissiondInfoByRole.CREATE && !permissiondInfoByRole.UPDATE) || !data,
          },
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Translations.newText"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/new`),
            disabled: !data,
          },
        ])}
      />

      <BoxFull className="dt-c my-4">
        {filteredData ? (
          <DataTable
            id='text-and-messages-front-datatable'
            header={
              <TableHeader
                title={t("Translations.translationsTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFiltersColumn}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Roles.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="functionality"
            sortOrder={1}
            filters={filters}
            value={filteredData.map((label: any) => ({
              ...label,
              functionality: label.widgets.join(", "),
              type: t(`Translations.${label.type.toLowerCase()}OptionLabel`),
              createdat: !!label?.createdat ? formatStringToDate2(label?.createdat, true, t) : null,
              updatedat: !!label?.updatedat ? formatStringToDate2(label?.updatedat, true, t) : null,
              actions: (
                <Actions
                  enableDelete={true}
                  onEdit={() => actionEditDetailFn(label, 'edit')}
                  onDelete={() => handleModalProps("DELETE", label)}
                  onDetail={() => actionEditDetailFn(label, 'detail')}
                  visibleObject={{...permissiondInfoByRole}}
                />
              ),
            }))}
            scrollable
            resizableColumns
            globalFilterFields={['functionality', 'key', 'esvalue', 'esnvalue', 'type', 'releaseversion', 'createdat', 'updatedat']}
            filterDisplay="menu"
            stateStorage="session"
            stateKey="filters-table"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? {
                      justifyContent: "center",
                      display: (
                        !permissiondInfoByRole.READ &&
                        !permissiondInfoByRole.UPDATE &&
                        !permissiondInfoByRole.REMOVE
                      ) ? 'none' : undefined,
                    }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field === "actions") ? "8.3rem" : "14rem",
                  display: (col.field === "actions")
                    ? ((
                        !permissiondInfoByRole.READ &&
                        !permissiondInfoByRole.UPDATE &&
                        !permissiondInfoByRole.REMOVE
                      ) ? 'none' : undefined)
                    : undefined,
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={col.field === "actions" ? false : true}
                filterElement={
                  ((col.field === "esvalue" || col.field === "envalue") && filterTemplate) ||
                  ((
                    col.field === "functionality" ||
                    col.field === "key" ||
                    col.field === "type" ||
                    col.field === "releaseversion" ||
                    col.field === "createdat" ||
                    col.field === "updatedat"
                  ) && multiselectFilterTemplate)
                }
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'isCustom', (rowData:any) => rowData)}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      <FilterDialog
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        data={data}
        setData={setFilteredData}
        onClearFilters={onClearFilters}
        form={form}
        formOptions={formOptions}
      />

      {data && importModalVisible && (
        <TranslationsImporter
          allowedIDs={data.map((label: any) => `${label.id}`)}
          columns={[
            {
              field: "id",
              header: "id",
            },
            ...columns
              .filter((col: any) => col?.field !== "actions")
              .map((col) => ({
                ...col,
                field: col?.field === "releaseversion" ? "version" : col?.field,
              })),
            {
              field: "description",
              header: t("Translations.descriptionLabel"),
            },
          ]}
          importFn={translationAction}
          reloadFn={reloadFn}
          setVisible={setImportModalVisible}
          auxModalController={setModalProps}
        />
      )}

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
